<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      <header class="sticky top-0 z-20 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
          @change-lang="changeLang"
          @logout="logout"
        />
      </header>

      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Dashboard Overview</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Last updated: {{ currentDate }}</p>
          </div>
          <button @click="fetchDashboardData" class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" :class="{'animate-spin': isRefreshing}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>

        <!-- Summary Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <!-- Total Staff -->
          <div @click="router.push('/staff')" class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700 transition-all cursor-pointer group">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Total Staff</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2 group-hover:text-blue-600">{{ data.summary.total || 0 }}</p>
              </div>
              <div class="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
              </div>
            </div>
          </div>

          <!-- Teaching Staff -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Teaching Staff</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ data.summary.teaching_total || 0 }}</p>
                <div class="flex flex-wrap gap-2 mt-3">
                  <span class="text-xs font-medium bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-md">N: {{ data.summary.teaching_nursery || 0 }}</span>
                  <span class="text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-1 rounded-md">P: {{ data.summary.teaching_primary || 0 }}</span>
                  <span class="text-xs font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-2 py-1 rounded-md">S: {{ data.summary.teaching_secondary || 0 }}</span>
                </div>
              </div>
              <div class="p-3 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
              </div>
            </div>
          </div>

          <!-- Outsourced Staff -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Outsourced Staff</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ data.summary.outsourced_total || 0 }}</p>
                <div class="flex flex-wrap gap-2 mt-3">
                  <span class="text-xs font-medium bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-1 rounded-md">Sec: {{ data.summary.outsourced_security || 0 }}</span>
                  <span class="text-xs font-medium bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-2 py-1 rounded-md">Clean: {{ data.summary.outsourced_cleaning || 0 }}</span>
                </div>
              </div>
              <div class="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              </div>
            </div>
          </div>

          <!-- Extracurricular Staff -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Extracurricular</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ data.summary.extra_total || 0 }}</p>
                <div class="flex flex-wrap gap-2 mt-3">
                  <span class="text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 px-2 py-1 rounded-md">Sports: {{ data.summary.extra_sports || 0 }}</span>
                  <span class="text-xs font-medium bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400 px-2 py-1 rounded-md">Arts: {{ data.summary.extra_arts || 0 }}</span>
                </div>
              </div>
              <div class="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs / Slides Section -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          <!-- Tab 1: Absenteeism & Pending Contracts -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <!-- Tab Headers -->
            <div class="flex border-b border-gray-200 dark:border-gray-700 mb-6">
              <button 
                @click="activeTab = 'absenteeism'"
                :class="`pb-3 px-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'absenteeism' ? 'border-red-500 text-red-600 dark:text-red-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`"
              >
                Absent Today (After 9 AM)
              </button>
              <button 
                @click="activeTab = 'contracts'"
                :class="`pb-3 px-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'contracts' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`"
              >
                Pending Contracts
              </button>
            </div>

            <!-- Absenteeism Content -->
            <div v-if="activeTab === 'absenteeism'">
              <div v-if="data.absenteeism.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                <svg class="w-12 h-12 mx-auto mb-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <p>All expected staff have clocked in today.</p>
              </div>
              <div v-else class="space-y-3">
                <div v-for="(person, index) in displayedAbsentees" :key="person.id" class="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-lg">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center text-red-600 dark:text-red-400 font-bold text-xs">
                      {{ person.full_name.charAt(0) }}
                    </div>
                    <div>
                      <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ person.full_name }}</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">{{ person.position_title }}</p>
                    </div>
                  </div>
                  <span class="text-xs font-medium px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 rounded">Absent</span>
                </div>
                
                <button v-if="data.absenteeism.length > 5" @click="showAllAbsent = !showAllAbsent" class="w-full mt-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors">
                  {{ showAllAbsent ? 'Show Less' : `View More (${data.absenteeism.length - 5} more)` }}
                </button>
              </div>
            </div>

            <!-- Pending Contracts Content -->
            <div v-if="activeTab === 'contracts'">
              <div v-if="data.pendingContracts.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>No pending contracts at this time.</p>
              </div>
              <div v-else class="space-y-3">
                <div v-for="person in data.pendingContracts" :key="person.id" class="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-lg">
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-xs">
                      {{ person.full_name.charAt(0) }}
                    </div>
                    <div>
                      <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ person.full_name }}</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">{{ person.position_title }}</p>
                    </div>
                  </div>
                  <span class="text-xs font-medium px-2 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 rounded">Contracting</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Tab 2: HR Action Required (Frequent Late/Absent) -->
          <div class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div class="flex justify-between items-center mb-6">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white">HR Action Required</h2>
              <span class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">Current Month</span>
            </div>
            
            <div v-if="data.frequentIssues.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>No staff with frequent attendance issues this month.</p>
            </div>
            <div v-else id="hrActionChart" class="w-full"></div>
          </div>

        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import ApexCharts from 'apexcharts';
import HrSidebar from '../../components/hr/HrSidebar.vue';
import HrTopbar from '../../components/hr/HrTopbar.vue';
import { useRouter } from 'vue-router';
import api from '@/services/api'; // Adjust path if needed

const router = useRouter();
const currentPageTitle = ref('HR Dashboard');
const isSidebarOpen = ref(true);
const isDarkMode = ref(false);
const isRefreshing = ref(false);
const activeTab = ref('absenteeism');
const showAllAbsent = ref(false);

const data = ref({
  summary: {},
  absenteeism: [],
  pendingContracts: [],
  frequentIssues: []
});

const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const displayedAbsentees = computed(() => {
  return showAllAbsent.value ? data.value.absenteeism : data.value.absenteeism.slice(0, 5);
});

let hrActionChart = null;

const fetchDashboardData = async () => {
  isRefreshing.value = true;
  try {
    const { data: response } = await api.get('get_hr_dashboard.php');
    if (response.success) {
      data.value = response.data;
      renderCharts();
    }
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
  } finally {
    setTimeout(() => { isRefreshing.value = false; }, 500);
  }
};

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.theme = 'light';
  }
  renderCharts();
};

const renderCharts = () => {
  if (hrActionChart) hrActionChart.destroy();

  const isDark = isDarkMode.value;
  const textColor = isDark ? '#9ca3af' : '#6b7280';
  const gridColor = isDark ? '#374151' : '#e5e7eb';

  const issues = data.value.frequentIssues;
  if (issues.length === 0) return;

  const hrOptions = {
    series: [{
      name: 'Incidents',
      data: issues.map(i => i.absent_days + i.late_days)
    }],
    chart: {
      type: 'bar',
      height: 300,
      toolbar: { show: false },
      background: 'transparent',
      foreColor: textColor
    },
    colors: ['#ef4444'], // Red for HR action
    plotOptions: {
      bar: { borderRadius: 4, horizontal: true, distributed: false, barHeight: '60%' }
    },
    dataLabels: { 
      enabled: true,
      formatter: function(val, opt) {
        const idx = opt.dataPointIndex;
        return `${issues[idx].absent_days} Absent, ${issues[idx].late_days} Late`;
      },
      style: { colors: [isDark ? '#fff' : '#333'], fontSize: '11px' },
      offsetX: 0
    },
    xaxis: {
      categories: issues.map(i => i.full_name),
      labels: { style: { colors: textColor, fontSize: '12px', fontWeight: 600 } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { labels: { style: { colors: textColor } } },
    grid: { borderColor: gridColor, strokeDashArray: 4 },
    tooltip: { 
      theme: isDark ? 'dark' : 'light',
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const item = issues[dataPointIndex];
        return `<div class="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg">
                  <p class="font-bold text-gray-900 dark:text-white">${item.full_name}</p>
                  <p class="text-sm text-red-600">Absent Days: ${item.absent_days}</p>
                  <p class="text-sm text-yellow-600">Late Days: ${item.late_days}</p>
                </div>`;
      }
    }
  };

  hrActionChart = new ApexCharts(document.querySelector("#hrActionChart"), hrOptions);
  hrActionChart.render();
};

onMounted(() => {
  fetchDashboardData();
  if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDarkMode.value = true;
    document.documentElement.classList.add('dark');
  }
});

watch(isDarkMode, () => { renderCharts(); });
</script>

<style scoped>
.bg-white, .bg-gray-50, .bg-gray-800, .bg-gray-900 { transition: background-color 0.3s ease; }
.text-gray-900, .text-gray-500, .text-gray-400 { transition: color 0.3s ease; }
.border-gray-100, .border-gray-200, .border-gray-300, .border-gray-700 { transition: border-color 0.3s ease; }
</style>